"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
const steps = [{
  id: 1,
  title: "Briefing",
  description: "Our LLMs analyze your brand identity and audience to craft a high-conversion script strategy."
}, {
  id: 2,
  title: "Storyboarding",
  description: "Visual synthesis engines generate cinematic frames and transitions tailored for mobile-first consumption."
}, {
  id: 3,
  title: "Rendering",
  description: "High-performance neural compute clusters process raw AI generation into high-fidelity 4K commercials."
}, {
  id: 4,
  title: "Delivery",
  description: "Social optimization algorithms format and tag your content for maximum algorithmic reach."
}];
export default function TheRoadToClosing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223476" id="our-neural-process" className="bg-background py-24 px-6 md:px-12 overflow-hidden" ref={ref}>
      <div className={`max-w-6xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary mb-6 tracking-tighter">
            Our Neural Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default font-medium">
            We merge cinematic production with advanced artificial intelligence 
            to disrupt the traditional video creation workflow.
          </p>
        </div>

        <div className="relative mb-20">
          {}
          <div className="absolute top-8 left-[10%] right-[10%] h-0.5 border-t-2 border-dashed border-secondary/50 z-0 hidden md:block" />

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-12 md:gap-4 relative z-10">
            <TooltipProvider delayDuration={200}>
              {steps.map((step, i) => <div key={step.id} data-index={i} className="flex flex-col items-center relative w-full md:w-1/4 group">
                  {}
                  {i !== steps.length - 1 && <div className="absolute top-16 left-1/2 w-0.5 h-[calc(100%+1rem)] border-l-2 border-dashed border-secondary/50 -translate-x-1/2 z-0 md:hidden" />}

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold font-heading shadow-[0_0_20px_rgba(0,255,255,0.3)] cursor-help z-10 relative transition-all duration-300 group-hover:scale-110 group-hover:bg-secondary group-hover:text-secondary-foreground group-hover:shadow-[0_0_25px_rgba(168,85,247,0.5)]">
                        0{step.id}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[250px] text-center p-4 shadow-2xl bg-card border-border backdrop-blur-xl" sideOffset={10}>
                      <p className="font-bold font-heading text-base mb-2 text-foreground">
                        {step.title}
                      </p>
                      <p className="text-sm font-mono opacity-90 leading-relaxed text-muted-foreground">
                        {step.description}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <div className="mt-6 text-center bg-card/40 backdrop-blur-md px-4 py-2 rounded-xl border border-border/50">
                    <h3 className="font-heading font-bold text-xl text-foreground mb-2 tracking-tight">
                      {step.title}
                    </h3>
                    {}
                    <p className="text-sm text-muted-foreground font-mono md:hidden max-w-[250px] mx-auto">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </TooltipProvider>
          </div>
        </div>

        <div className="text-center">
          <Button asChild size="lg" className="rounded-full px-8 text-base shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all hover:shadow-primary/20">
            <Link to="#">
              Initiate Production
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}